import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { WalletItemSSN } from "../../../store/common";
import * as httpClient from "../../../util/httpClient";
import { Notification } from "../../common/Notification";
import { useForm } from "react-hook-form";
import { getFlowByName } from "../../../config/FlowName";
import * as NextStepFlowAction from "../../../store/nextStepFlowService/actions";
import { ApplicationState } from "../../../store";
import { PATTERNS } from "../../../util/patterns";
import { useTranslation } from "react-i18next";

const SocialSecurityCardDialog: React.FunctionComponent<{ requestDataCallback(): void; walletItem: WalletItemSSN; isNew: boolean; closeDialog(): void }> = (
    props
) => {
    const { t: tLang, i18n: i18nLang } = useTranslation();
    const [isLoading, setisLoading] = useState(false);

    const dispatch = useDispatch();
    const lostWalletFlow = useSelector((state: ApplicationState) => getFlowByName(state, "LOST-WALLET-ASSISTANCE"));
    const completeGetStartedItem = (flowID: any, flowName: string) => dispatch(NextStepFlowAction.actionCreators.completeGetStartedItem(flowID, flowName));
    const [ssn, setSSN] = useState(props.walletItem);
    const [errorMsg, seterrorMsg] = useState("");
    const [hasError, setHasError] = useState(false);

    /**Validation with react-hook-form ***/
    const { register, errors, formState, handleSubmit } = useForm({
        mode: "onBlur",
        reValidateMode: "onChange",
    });

    const { isValid, isSubmitting } = formState;

    const closeDialog = () => {
        document.body.classList.remove("no_scroll");
        props.closeDialog();
    };

    const refresWalletItem = () => {
        closeDialog();
        props.requestDataCallback();
    };

    function onChangeHandler(event: any) {
        setHasError(false);
        const elName = event.currentTarget.name;
        setSSN({ ...ssn, [elName as string]: event.currentTarget.value });
    }

    const saveItem = () => {
        const requestBody = {
            "lost-wallet": {
                "lost-wallet-ssn": ssn,
            },
        };
        setisLoading(true);
        httpClient
            .sendRequest("POST", "api/lostWallet/" + (props.isNew ? "addNewWalletItem" : "updateWalletItem"), requestBody)
            .then((data) => {
                setisLoading(false);
                if (data.isSuccess) {
                    if (lostWalletFlow && lostWalletFlow.flowStepStatus !== "COMPLETED") {
                        completeGetStartedItem(lostWalletFlow.flowStepId, lostWalletFlow.flowName ? lostWalletFlow.flowName : " ");
                    }
                    Notification({
                        type: "success",
                        title: props.isNew
                            ? tLang("identityVault.secureStorage.lostWallterWidget.socialSecurityCard.addToastTitle")
                            : tLang("identityVault.secureStorage.lostWallterWidget.socialSecurityCard.updateToastTitle"),
                        message: props.isNew
                            ? tLang("identityVault.secureStorage.lostWallterWidget.socialSecurityCard.addToastMessage")
                            : tLang("identityVault.secureStorage.lostWallterWidget.socialSecurityCard.updateToastMessage"),
                    });
                    refresWalletItem();
                } else if (data.hasError && data.errors.length > 0) {
                    seterrorMsg(data.errors.join(", "));
                    setHasError(true);
                } else {
                    seterrorMsg(tLang("identityVault.secureStorage.lostWallterWidget.socialSecurityCard.unknownError"));
                    setHasError(true);
                }
            })
            .catch(function (error) {
                setisLoading(false);
                Notification({
                    type: "error",
                    title: tLang("identityVault.secureStorage.lostWallterWidget.socialSecurityCard.errorToastTitle"),
                    message: tLang("identityVault.secureStorage.lostWallterWidget.socialSecurityCard.errorToastMesage"),
                    duration: 0,
                });
                closeDialog();
            });
    };

    return (
        <React.Fragment>
            {!isLoading ? (
                <>
                    <div className="dialog-header">
                        <div className="dialog-icon">
                            <em className={props.isNew ? "las la-plus" : "las la-edit"}></em>
                        </div>
                        <h1>
                            {props.isNew
                                ? tLang("identityVault.secureStorage.lostWallterWidget.socialSecurityCard.addLabel")
                                : tLang("identityVault.secureStorage.lostWallterWidget.socialSecurityCard.editLabel")}{" "}
                            {tLang("identityVault.secureStorage.lostWallterWidget.socialSecurityCard.title")}
                        </h1>
                    </div>

                    <form onSubmit={handleSubmit(saveItem)} id="ssn-form-container">
                        <div className="dialog-body">
                            <div className={errors["ssn"] ? "form-row row status-error" : "form-row row"}>
                                <div className="form-row inline row">
                                    <div className="form-label col-xs-12 col-sm-5 col-no-gutter">
                                        <label htmlFor="edit-ssn">
                                            {tLang("identityVault.secureStorage.lostWallterWidget.socialSecurityCard.label.ssn")}{" "}
                                            <span className="required" title="Mandatory">
                                                *
                                            </span>
                                        </label>
                                    </div>
                                    <div role="alert" className="form-input col-xs-12 col-sm-7 col-no-gutter">
                                        <input
                                            aria-required="true"
                                            type="text"
                                            className="inputbox"
                                            id="edit-ssn"
                                            aria-labelledby="edit-ssn"
                                            name="ssn"
                                            defaultValue={ssn.ssn}
                                            onChange={onChangeHandler}
                                            ref={register({
                                                required: true,
                                                pattern: /^$|^[(]?\d{3}[)]?[-]?\d{2}[-]?\d{4}$/,
                                            })}
                                        />

                                        {errors.ssn && errors.ssn.type === "pattern" && (
                                            <span className="form-error">
                                                {tLang("identityVault.secureStorage.lostWallterWidget.socialSecurityCard.validationMessage.ssn.pattern")}
                                            </span>
                                        )}

                                        {errors.ssn && errors.ssn.type === "required" && (
                                            <span className="form-error">
                                                {tLang("identityVault.secureStorage.lostWallterWidget.socialSecurityCard.validationMessage.ssn.required")}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className={errors["firstName"] ? "form-row row status-error" : "form-row row"}>
                                <div className="form-row inline row">
                                    <div className="form-label col-xs-12 col-sm-5 col-no-gutter">
                                        <label htmlFor="edit-first-name">
                                            {tLang("identityVault.secureStorage.lostWallterWidget.socialSecurityCard.label.firstName")}{" "}
                                            <span className="required" title="Mandatory">
                                                *
                                            </span>
                                        </label>
                                    </div>
                                    <div role="alert" className="form-input col-xs-12 col-sm-7 col-no-gutter">
                                        <input
                                            aria-required="true"
                                            type="text"
                                            className="inputbox"
                                            id="edit-first-name"
                                            aria-labelledby="edit-first-name"
                                            name="firstName"
                                            defaultValue={ssn.firstName}
                                            onChange={onChangeHandler}
                                            ref={register({
                                                required: true,
                                                pattern: PATTERNS.FIRSTNAME.pattern,
                                            })}
                                            maxLength={30}
                                        />
                                        {errors.firstName && errors.firstName.type === "pattern" && (
                                            <span className="form-error">
                                                {tLang("identityVault.secureStorage.lostWallterWidget.socialSecurityCard.validationMessage.firstName.pattern")}
                                            </span>
                                        )}

                                        {errors.firstName && errors.firstName.type === "required" && (
                                            <span className="form-error">
                                                {tLang("identityVault.secureStorage.lostWallterWidget.socialSecurityCard.validationMessage.firstName.required")}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className={errors["middleName"] ? "form-row row status-error" : "form-row row"}>
                                <div className="form-row inline row">
                                    <div className="form-label col-xs-12 col-sm-5 col-no-gutter">
                                        <label htmlFor="edit-middle-initial">
                                            {tLang("identityVault.secureStorage.lostWallterWidget.socialSecurityCard.label.middleInitial")}
                                        </label>
                                    </div>
                                    <div role="alert" className="form-input col-xs-12 col-sm-7 col-no-gutter">
                                        <input
                                            aria-required="true"
                                            type="text"
                                            className="inputbox"
                                            id="edit-middle-initial"
                                            aria-labelledby="edit-middle-initial"
                                            name="middleName"
                                            defaultValue={ssn.middleName}
                                            onChange={onChangeHandler}
                                            ref={register({
                                                pattern: PATTERNS.MIDDLE_INITIAL.pattern,
                                            })}
                                            maxLength={1}
                                        />
                                        {errors.middleName && errors.middleName.type === "pattern" && (
                                            <span className="form-error">
                                                {tLang("identityVault.secureStorage.lostWallterWidget.socialSecurityCard.validationMessage.middleInitial.pattern")}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className={errors["lastName"] ? "form-row row status-error" : "form-row row"}>
                                <div className="form-row inline row">
                                    <div className="form-label col-xs-12 col-sm-5 col-no-gutter">
                                        <label htmlFor="edit-last-name">
                                            {tLang("identityVault.secureStorage.lostWallterWidget.socialSecurityCard.label.lastName")}{" "}
                                            <span className="required" title="Mandatory">
                                                *
                                            </span>
                                        </label>
                                    </div>
                                    <div role="alert" className="form-input col-xs-12 col-sm-7 col-no-gutter">
                                        <input
                                            aria-required="true"
                                            type="text"
                                            className="inputbox"
                                            id="edit-last-name"
                                            aria-labelledby="edit-last-name"
                                            name="lastName"
                                            defaultValue={ssn.lastName}
                                            onChange={onChangeHandler}
                                            ref={register({
                                                required: true,
                                                pattern: PATTERNS.LASTNAME.pattern,
                                            })}
                                            maxLength={30}
                                        />
                                        {errors.lastName && errors.lastName.type === "pattern" && (
                                            <span className="form-error">
                                                {tLang("identityVault.secureStorage.lostWallterWidget.socialSecurityCard.validationMessage.lastName.pattern")}
                                            </span>
                                        )}

                                        {errors.lastName && errors.lastName.type === "required" && (
                                            <span className="form-error">
                                                {tLang("identityVault.secureStorage.lostWallterWidget.socialSecurityCard.validationMessage.lastName.required")}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="form-row inline row">
                                <div className="form-label col-xs-12 col-sm-5 col-no-gutter">
                                    <label htmlFor="edit-suffix">{tLang("identityVault.secureStorage.lostWallterWidget.socialSecurityCard.label.suffix")}</label>
                                </div>
                                <div className="form-input col-xs-12 col-sm-7 col-no-gutter">
                                    <select
                                        className="inputbox"
                                        id="edit-suffix"
                                        aria-labelledby="edit-suffix"
                                        name="suffix"
                                        defaultValue={ssn.suffix}
                                        onChange={onChangeHandler}
                                    >
                                        <option value="">{tLang("identityVault.secureStorage.lostWallterWidget.socialSecurityCard.dropdownHeaders.suffix")}</option>
                                        <option value="Sr">Sr</option>
                                        <option value="Jr">Jr</option>
                                        <option value="II">II</option>
                                        <option value="III">III</option>
                                        <option value="IV">IV</option>
                                    </select>
                                </div>
                            </div>

                            <div className="form-row inline row">
                                <div className="form-label col-xs-12 col-sm-5 col-no-gutter">
                                    <label htmlFor="edit-notes">{tLang("identityVault.secureStorage.lostWallterWidget.socialSecurityCard.label.notes")}</label>
                                </div>
                                <div className="form-input col-xs-12 col-sm-7 col-no-gutter">
                                    <textarea
                                        className="inputbox"
                                        id="edit-notes"
                                        aria-labelledby="edit-notes"
                                        name="notes"
                                        maxLength={500}
                                        style={{ resize: "none" }}
                                        placeholder=""
                                        rows={6}
                                        defaultValue={ssn.notes}
                                        onChange={onChangeHandler}
                                    ></textarea>
                                </div>
                            </div>

                            {hasError && (
                                <div className="form-row inline row">
                                    <div className="form-label col-xs-12 col-sm-4 col-no-gutter"></div>
                                    <div className="form-input col-xs-12 col-sm-8 col-no-gutter">
                                        <p className="form-error">{errorMsg}</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="dialog-footer">
                            <div className="row between-xs">
                                <div className="col-xs col-no-gutter flex-grow-null">
                                    <button type="button" className="btn-default btn-link btn-no-padding btn-no-border" onClick={() => closeDialog()}>
                                        <span className="btn-text">
                                            {tLang("identityVault.secureStorage.lostWallterWidget.socialSecurityCard.label.cancelButtonText")}
                                        </span>
                                    </button>
                                </div>
                                <div className="col-xs col-no-gutter flex-grow-null">
                                    <button type="submit" className="btn-default btn-primary" disabled={isSubmitting}>
                                        <span className="btn-text">
                                            {tLang("identityVault.secureStorage.lostWallterWidget.socialSecurityCard.label.saveButtonText")}
                                        </span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>
                </>
            ) : (
                <div className="box loading"></div>
            )}
        </React.Fragment>
    );
};

export default SocialSecurityCardDialog;
